import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation";

export const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

export const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const BODY_FONT = { name: "Calibri", size: 11 };

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;

export const TITLE_ROW = 3;
export const HEADER_ROW = 4;
export const DATA_START_ROW = 5;

export const MONETARY_HEADERS = [
  { col: COL_D, label: "Sr. No.", width: 8 },
  { col: COL_E, label: "NGRBC Principle", width: 16 },
  {
    col: COL_F,
    label: "Name of the regulatory/ enforcement agencies/ judicial institutions",
    width: 28,
  },
  { col: COL_G, label: "Amount (In INR)", width: 14 },
  { col: COL_H, label: "Brief of the Case", width: 24 },
  { col: COL_I, label: "Has an appeal been preferred? (Yes/No)", width: 18 },
];

export const NON_MONETARY_HEADERS = [
  { col: COL_D, label: "Sr. No.", width: 8 },
  { col: COL_E, label: "NGRBC Principle", width: 16 },
  {
    col: COL_F,
    label: "Name of the regulatory/ enforcement agencies/ judicial institutions",
    width: 28,
  },
  { col: COL_G, label: "Brief of the Case", width: 28 },
  { col: COL_H, label: "Has an appeal been preferred? (Yes/No)", width: 18 },
];

export const APPEAL_HEADERS = [
  { col: COL_D, label: "Sr. No.", width: 8 },
  {
    col: COL_E,
    label: "Name of the regulatory/ enforcement agencies/ judicial institutions",
    width: 32,
  },
  { col: COL_F, label: "Case Details", width: 36 },
];

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align || "center",
    vertical: "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "-";
}

function formatAmount(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (!Number.isNaN(num)) return num.toFixed(2);
  return String(value);
}

function formatNgrbcPrinciple(value) {
  if (value === null || value === undefined || value === "") return "-";
  const str = String(value).trim();
  if (/^P\d+$/i.test(str)) return str.replace(/^P/i, "Principle ");
  return str;
}

export function toRows(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter((item) => item && typeof item === "object");
  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (!keys.length) return [];
    const looksLikeRecord =
      "ngrbcPrinciple" in data ||
      "regulatoryAgency" in data ||
      "caseBrief" in data ||
      "regulatoryEnforcement" in data ||
      "caseDetails" in data;
    return looksLikeRecord ? [data] : keys.map((key) => data[key]).filter(Boolean);
  }
  return [];
}

function setColumnWidths(ws, headers) {
  const widths = { A: 2, B: 2, C: 2 };
  headers.forEach(({ col, width }) => {
    widths[String.fromCharCode(64 + col)] = width;
  });
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
}

export function buildMonetaryDetailSheet(workbook, sheetName, title, rows) {
  const ws = workbook.addWorksheet(sheetName, { views: [{ showGridLines: true }] });
  setColumnWidths(ws, MONETARY_HEADERS);
  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_1, { mergeEnd: COL_I });

  ws.mergeCells(TITLE_ROW, COL_D, TITLE_ROW, COL_I);
  setCell(ws, TITLE_ROW, COL_D, title, { bold: true, fill: HEADER_FILL, align: "center" });

  MONETARY_HEADERS.forEach(({ col, label }) => {
    setCell(ws, HEADER_ROW, col, label, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
  });

  const dataRows = rows.length ? rows : [{}];
  dataRows.forEach((item, idx) => {
    const row = DATA_START_ROW + idx;
    setCell(ws, row, COL_D, String(idx + 1), { align: "center" });
    setCell(ws, row, COL_E, formatNgrbcPrinciple(item?.ngrbcPrinciple), { align: "left" });
    setCell(ws, row, COL_F, item?.regulatoryAgency || "-", { align: "left" });
    setCell(ws, row, COL_G, formatAmount(item?.amountInINR), { align: "right" });
    setCell(ws, row, COL_H, item?.caseBrief || "-", { align: "left" });
    setCell(ws, row, COL_I, toYesNo(item?.hasAppealPreferred), { align: "center" });
  });

  return ws;
}

export function buildNonMonetaryDetailSheet(workbook, sheetName, title, rows) {
  const ws = workbook.addWorksheet(sheetName, { views: [{ showGridLines: true }] });
  setColumnWidths(ws, NON_MONETARY_HEADERS);
  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_1, { mergeEnd: COL_H });

  ws.mergeCells(TITLE_ROW, COL_D, TITLE_ROW, COL_H);
  setCell(ws, TITLE_ROW, COL_D, title, { bold: true, fill: HEADER_FILL, align: "center" });

  NON_MONETARY_HEADERS.forEach(({ col, label }) => {
    setCell(ws, HEADER_ROW, col, label, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
  });

  const dataRows = rows.length ? rows : [{}];
  dataRows.forEach((item, idx) => {
    const row = DATA_START_ROW + idx;
    setCell(ws, row, COL_D, String(idx + 1), { align: "center" });
    setCell(ws, row, COL_E, formatNgrbcPrinciple(item?.ngrbcPrinciple), { align: "left" });
    setCell(ws, row, COL_F, item?.regulatoryAgency || "-", { align: "left" });
    setCell(ws, row, COL_G, item?.caseBrief || "-", { align: "left" });
    setCell(ws, row, COL_H, toYesNo(item?.hasAppealPreferred), { align: "center" });
  });

  return ws;
}

export function buildAppealDetailSheet(workbook, sheetName, title, rows) {
  const ws = workbook.addWorksheet(sheetName, { views: [{ showGridLines: true }] });
  setColumnWidths(ws, APPEAL_HEADERS);
  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_1, { mergeEnd: COL_F });

  ws.mergeCells(TITLE_ROW, COL_D, TITLE_ROW, COL_F);
  setCell(ws, TITLE_ROW, COL_D, title, { bold: true, fill: HEADER_FILL, align: "center" });

  APPEAL_HEADERS.forEach(({ col, label }) => {
    setCell(ws, HEADER_ROW, col, label, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
  });

  const dataRows = rows.length ? rows : [{}];
  dataRows.forEach((item, idx) => {
    const row = DATA_START_ROW + idx;
    setCell(ws, row, COL_D, String(idx + 1), { align: "center" });
    setCell(ws, row, COL_E, item?.regulatoryEnforcement || "-", { align: "left" });
    setCell(ws, row, COL_F, item?.caseDetails || "-", { align: "left" });
  });

  return ws;
}
